// Importing babel/polyfill to get ECMA6 features to work.
// This needs to be imported at the top of the application
// entry point, per docs. This is necessary because we are
// using some ECMA6 features, in particular Promises that
// are not supported by all browsers, for e.g. IE 10/11.
import 'babel-polyfill';

import AnswerPage from './answer-page.jsx';
import AnswerStore from './answer-store';
import AppInit from './app-init';
import Error from './error.jsx';
import HomePage from './home-page.jsx';
import Spinner from './spinner.jsx';

import Flux from 'flux';
const Dispatcher = Flux.Dispatcher;

import React from 'react';

import Router from 'react-router';
const Route = Router.Route;
const DefaultRoute = Router.DefaultRoute;
const RouteHandler = Router.RouteHandler;

/**
 * Top Level component for the Aristo Demo app.
 * Renders the invariant portions of the demo and handles all client side
 * routes.
 * This does not extend ImmutableRenderComponent because currently it is
 * not compatible with React Router and stops routes from being handled
 * correctly.
 */
export default class AristoDemo extends React.Component {
  constructor(props) {
    super(props);
    // Dispatcher to be passed to the ConfigStore.
    this.dispatcher = new Dispatcher();
    // AnswerStore, which forwards user's selected question to demo server which gets
    // back Controller Response.
    this.answerStore = new AnswerStore(this.dispatcher);

    // The state contains the current demo mode (Debug/Regular) and the Config obtained from AppInit.
    // It also has a configError flag which gets set to true if AppInit
    // returned a failed promise on the Config object.
    this.state = { config: undefined, configError: false };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.func.isRequired,
    };
  }

  static get propTypes() {
    return {
      query: React.PropTypes.object,
      routerState: React.PropTypes.object,
    };
  }

  componentDidMount() {
    // Call AppInit to get Config info. Then set state with the obtained Config.
    AppInit.getConfig().then((config) => {
      this.setState({ config: config })
    }).catch(e => {
      console.log(e);
      this.setState({ configError: true });
    });
  }

  /**
   * Renders the invariant portion of the demo-- the contact header,
   * AI2 banner and the ARISTO heading.
   * Demo is available only after Config has been successfully obtained.
   * Until then it will show the "Loading" message;
   */
  render() {
    // Aristo Demo Main JSX to render
    let aristoDemo = null;

    if (this.state.config) {
      const featuredQuestions = this.state.config.featuredQuestions;
      aristoDemo = (
        <RouteHandler
          dispatcher={this.dispatcher}
          featuredQuestions={featuredQuestions}
          demoSolvers={this.state.config.demoSolvers}
          answerStore={this.answerStore} />);
    } else {
      if (this.state.configError) {
        // Set Error Message for if Config was not successfully obtained.
        const errorTitle = "Aristo Demo is currently unavailable!";
        const error = "Sorry, an error was encountered while starting up Aristo Demo. Please try again later.";
        aristoDemo = (<div className="aristo"><Error title={errorTitle} error={error} /></div>);
      } else {
        aristoDemo = (<div className="aristo"><Spinner /></div>);
      }
    }

    return (
      <div className="aristo-demo" onKeyUp={this.handleKeyPress}>
        <header className="application-header">
          <a className="logo" width="440" height="48px" target="_blank" href="http://allenai.org"><img src="/assets/images/ai2_logo.svg" alt="AI2 logo" /></a>
          <div className="ari-title-bar">
            <div className="title">
              <img src="assets/images/aristo_logo_small.png" alt="Aristo logo" width="350px" height="75px" />
            </div>
            <div className="tagline">An intelligent system that reads, learns, and reasons about science.</div>
            <div className="top-disclaimer">This demo represents a selection of the various solvers we have developed for the Aristo project over the years, demonstrating a variety of question answering techniques.</div>
            <a href="http://allenai.org/aristo/" target="_blank" className="text-orange">Learn More...</a>
          </div>
        </header>
        <section className="application-view">
          {aristoDemo}
        </section>
        <footer className="application-footer">
          © {new Date().getFullYear()} The Allen Institute for Artificial Intelligence
         </footer>
      </div>
    );
  }
}

/**
 * Routes handled.
 * Default route renders the landing page with the set of featured questions.
 * /ask route takes a selected question and passed it to AnswerPage.
 */
const routes = (
  <Route name="app" path="/" handler={AristoDemo}>
    <DefaultRoute handler={HomePage} />
    <Route name="ask" path="ask" handler={AnswerPage} />
  </Route>
);

Router.run(routes, Router.HistoryLocation, (Root, routerState) => {
  React.render(<Root routerState={routerState} />, document.body);
});
